
// Agent template definitions for different industries and use cases

// Type definitions for agent templates and settings
export interface AgentTemplateSetting {
  id: string;
  label: string;
  type: 'text' | 'textarea';
  default: string;
}

export interface AgentTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  settings: AgentTemplateSetting[];
}

// Extended template list with industry-specific options
const AGENT_TEMPLATES: AgentTemplate[] = [
  // General templates
  {
    id: 'chatbot',
    name: 'AI Chatbot',
    category: 'general',
    description: 'A simple conversational AI for answering questions',
    settings: [
      { id: 'persona', label: 'Chatbot Persona', type: 'text', default: 'Helpful assistant' },
      { id: 'knowledge', label: 'Knowledge Base', type: 'textarea', default: 'General knowledge' }
    ]
  },
  {
    id: 'customer-support',
    name: 'Customer Support Agent',
    category: 'general',
    description: 'An agent designed to handle customer inquiries and support tickets',
    settings: [
      { id: 'company', label: 'Company Name', type: 'text', default: 'Your Company' },
      { id: 'products', label: 'Products/Services', type: 'textarea', default: 'List your products or services here' },
      { id: 'faqs', label: 'FAQs', type: 'textarea', default: 'Common questions and answers' }
    ]
  },
  {
    id: 'code-assistant',
    name: 'Code Assistant',
    category: 'general',
    description: 'A specialized agent for helping with programming tasks',
    settings: [
      { id: 'languages', label: 'Programming Languages', type: 'text', default: 'Python, JavaScript, TypeScript' },
      { id: 'expertise', label: 'Areas of Expertise', type: 'textarea', default: 'Web development, algorithms, data structures' }
    ]
  },
  
  // Banking Industry
  {
    id: 'loan-processor',
    name: 'Loan Processing SOP Agent',
    category: 'banking',
    description: 'Guides loan officers through the proper loan application processing procedures',
    settings: [
      { id: 'bankName', label: 'Bank Name', type: 'text', default: 'Your Bank' },
      { id: 'loanTypes', label: 'Loan Types', type: 'textarea', default: 'Mortgage, Personal, Auto, Business' },
      { id: 'requirements', label: 'Documentation Requirements', type: 'textarea', default: 'ID verification, credit history, income verification' },
      { id: 'complianceRules', label: 'Compliance Requirements', type: 'textarea', default: 'KYC procedures, AML checks, regulatory requirements' }
    ]
  },
  {
    id: 'fraud-detection',
    name: 'Fraud Detection Protocol Agent',
    category: 'banking',
    description: 'Assists in following fraud investigation and reporting procedures',
    settings: [
      { id: 'alertThresholds', label: 'Alert Thresholds', type: 'textarea', default: 'Unusual transaction patterns, large withdrawals, foreign transactions' },
      { id: 'investigationSteps', label: 'Investigation Steps', type: 'textarea', default: 'Customer verification, transaction review, account lockdown procedures' },
      { id: 'reportingRequirements', label: 'Reporting Requirements', type: 'textarea', default: 'Internal documentation, regulatory reporting timelines' }
    ]
  },
  
  // Insurance Industry
  {
    id: 'claims-processor',
    name: 'Claims Processing SOP Agent',
    category: 'insurance',
    description: 'Guides agents through proper insurance claims processing procedures',
    settings: [
      { id: 'company', label: 'Insurance Company', type: 'text', default: 'Your Insurance Company' },
      { id: 'claimTypes', label: 'Claim Types', type: 'textarea', default: 'Auto, Home, Health, Life' },
      { id: 'verificationSteps', label: 'Verification Steps', type: 'textarea', default: 'Policy verification, coverage check, documentation review' },
      { id: 'adjustmentGuidelines', label: 'Adjustment Guidelines', type: 'textarea', default: 'Damage assessment, valuation methods, payout limits' }
    ]
  },
  {
    id: 'policy-underwriting',
    name: 'Policy Underwriting SOP Agent',
    category: 'insurance',
    description: 'Assists in following underwriting procedures for new insurance policies',
    settings: [
      { id: 'riskAssessment', label: 'Risk Assessment Criteria', type: 'textarea', default: 'Credit score thresholds, medical history flags, property risk factors' },
      { id: 'pricingModels', label: 'Pricing Models', type: 'textarea', default: 'Premium calculation methods, discount eligibility, risk adjustments' },
      { id: 'approvalLevels', label: 'Approval Levels', type: 'textarea', default: 'Automatic approval conditions, manual review triggers, escalation paths' }
    ]
  },
  
  // Healthcare Industry  
  {
    id: 'patient-intake',
    name: 'Patient Intake SOP Agent',
    category: 'healthcare',
    description: 'Guides staff through proper patient onboarding and information collection procedures',
    settings: [
      { id: 'facilityName', label: 'Facility Name', type: 'text', default: 'Your Healthcare Facility' },
      { id: 'requiredForms', label: 'Required Forms', type: 'textarea', default: 'Patient history, insurance information, consent forms, HIPAA acknowledgment' },
      { id: 'triageProtocol', label: 'Triage Protocol', type: 'textarea', default: 'Urgency assessment, chief complaint documentation, vital signs protocol' },
      { id: 'privacyProcedures', label: 'Privacy Procedures', type: 'textarea', default: 'Data handling guidelines, confidentiality steps, secure information storage' }
    ]
  },
  {
    id: 'medication-administration',
    name: 'Medication Administration SOP',
    category: 'healthcare',
    description: 'Ensures proper procedures for medication verification, preparation, and administration',
    settings: [
      { id: 'verificationSteps', label: 'Verification Steps', type: 'textarea', default: 'Five Rights check, patient identification, medication reconciliation' },
      { id: 'administrationProtocols', label: 'Administration Protocols', type: 'textarea', default: 'Route-specific procedures, timing requirements, monitoring guidelines' },
      { id: 'documentationRequirements', label: 'Documentation Requirements', type: 'textarea', default: 'Pre/post administration documentation, adverse event reporting, MAR completion' }
    ]
  },
  
  // Real Estate
  {
    id: 'property-listing',
    name: 'Property Listing SOP Agent',
    category: 'real-estate',
    description: 'Guides real estate agents through proper property listing procedures',
    settings: [
      { id: 'agency', label: 'Agency Name', type: 'text', default: 'Your Real Estate Agency' },
      { id: 'documentationChecklist', label: 'Documentation Checklist', type: 'textarea', default: 'Owner verification, property details, disclosure requirements' },
      { id: 'photoPolicies', label: 'Photography Requirements', type: 'textarea', default: 'Required angles, staging guidelines, photo processing standards' },
      { id: 'pricingStrategy', label: 'Pricing Strategy Guidelines', type: 'textarea', default: 'Comparative market analysis, pricing factors, negotiation parameters' }
    ]
  },
  
  // Custom Template
  {
    id: 'custom',
    name: 'Custom SOP Agent',
    category: 'custom',
    description: 'Build a completely customized SOP agent for your specific needs',
    settings: [
      { id: 'agentName', label: 'Agent Name', type: 'text', default: 'Custom SOP Agent' },
      { id: 'industry', label: 'Industry', type: 'text', default: '' },
      { id: 'purpose', label: 'Purpose', type: 'textarea', default: 'Describe the purpose of this SOP agent' },
      { id: 'procedureSteps', label: 'Procedure Steps', type: 'textarea', default: 'List the steps of your procedure in detail' },
      { id: 'complianceRequirements', label: 'Compliance Requirements', type: 'textarea', default: 'List any compliance requirements or regulations' }
    ]
  }
];

export default AGENT_TEMPLATES;
